/**
 * run-checkbox-group — Wireframe skin module
 *
 * Exports { styles, render } per SkinModule contract.
 * Same DOM structure as default skin. Minimal chrome, grayscale, dashed borders.
 *
 * Reads host properties read-only. Never writes host state.
 */

export const styles = `
  *, *::before, *::after { box-sizing: border-box; }

  :host {
    display: block;
  }

  :host([disabled]) {
    opacity: 0.4;
    pointer-events: none;
  }

  [part="base"] {
    display: flex;
    flex-direction: column;
    gap: var(--run-checkbox-group-label-gap, 4px);
    border: 1px dashed #ccc;
    border-radius: 4px;
    padding: 8px;
  }

  [part="base"][data-invalid] {
    border-color: #666;
    border-width: 2px;
  }

  [part="label"] {
    color: #333;
    font-weight: 500;
    font-family: monospace;
  }

  [part="label"]:empty {
    display: none;
  }

  .select-all-container {
    padding-bottom: 4px;
    border-bottom: 1px dashed #ccc;
    margin-bottom: 4px;
  }

  .select-all-container:empty {
    display: none;
  }

  [part="children"] {
    display: flex;
    gap: var(--run-checkbox-group-gap, 8px);
  }

  [part="children"][data-orientation="vertical"] {
    flex-direction: column;
  }

  [part="children"][data-orientation="horizontal"] {
    flex-direction: row;
    flex-wrap: wrap;
  }

  [part="error"] {
    color: #666;
    font-size: 0.875em;
    font-family: monospace;
  }

  [part="error"]:not([data-visible]) {
    display: none;
  }
`;

export function render(host) {
  const sr = host.shadowRoot;

  // Create skeleton only on first render
  if (!sr.querySelector('[part="base"]')) {
    sr.innerHTML = `
      <div part="base">
        <div part="label" id="${host.localName}-label-${Date.now()}">
          <slot name="label"></slot>
        </div>
        <div class="select-all-container">
          <slot name="select-all"></slot>
        </div>
        <div part="children">
          <slot></slot>
        </div>
        <div part="error">
          <slot name="error"></slot>
        </div>
      </div>`;
  }

  // Patch attributes — same logic as default skin
  const base = sr.querySelector('[part="base"]');
  const children = sr.querySelector('[part="children"]');
  const error = sr.querySelector('[part="error"]');

  base.setAttribute('data-orientation', host.orientation || 'vertical');
  base.toggleAttribute('data-disabled', !!host.disabled);
  base.toggleAttribute('data-readonly', !!host.readonly);
  base.toggleAttribute('data-invalid', !!host.invalid);
  base.toggleAttribute('data-required', !!host.required);

  children.setAttribute('data-orientation', host.orientation || 'vertical');
  error.toggleAttribute('data-visible', !!host.invalid);
}
